/**
 * Cybernetic DEFINITIONS registry — bionic replacements.
 *
 * Each entry is keyed by the sourceId from items.json and contains
 * craftsmanship-variant mechanical effects per the Rogue Trader rulebook.
 *
 * Populated incrementally across tasks 5.1–5.4.
 */

import { BodyLocation, WeaponClass } from '../types/character';
import type { CyberneticDefinition } from './CyberneticsService';

export const CYBERNETIC_DEFINITIONS: Record<string, CyberneticDefinition> = {
  // -----------------------------------------------------------------
  // Bionic Arm
  // -----------------------------------------------------------------
  'bionic-arm': {
    id: 'bionic-arm',
    name: 'Bionic Arm',
    defaultLocation: 'Right Arm',
    craftsmanshipEffects: {
      Poor: {
        modifiers: [
          { targetId: 'weaponSkill', value: -10, condition: 'Bionic Arm (Poor) — using this arm' },
          {
            targetId: 'ballisticSkill',
            value: -10,
            condition: 'Bionic Arm (Poor) — using this arm'
          },
          { targetId: 'agility', value: -10, condition: 'Bionic Arm (Poor) — delicate tasks' }
        ],
        specialAbilities: ['Immune to effects targeting organic arm']
      },
      Common: {
        specialAbilities: ['Immune to effects targeting organic arm']
      },
      Good: {
        modifiers: [
          { targetId: 'agility', value: 10, condition: 'Bionic Arm (Good) — delicate tasks' },
          { targetId: 'strength', value: 10, condition: 'Bionic Arm (Good) — using this arm' }
        ],
        specialAbilities: ['Immune to effects targeting organic arm']
      },
      Best: {
        modifiers: [
          { targetId: 'agility', value: 10, condition: 'Bionic Arm (Best) — delicate tasks' },
          { targetId: 'strength', value: 10, condition: 'Bionic Arm (Best) — using this arm' }
        ],
        specialAbilities: [
          'Immune to effects targeting organic arm',
          '+10 to tests requiring fine manipulation'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Bionic Locomotion (Bionic Legs)
  // -----------------------------------------------------------------
  'bionic-locomotion': {
    id: 'bionic-locomotion',
    name: 'Bionic Locomotion',
    defaultLocation: 'Right Leg',
    craftsmanshipEffects: {
      Poor: {
        modifiers: [
          {
            targetId: 'agility',
            value: -10,
            condition: 'Bionic Locomotion (Poor) — movement-related tests'
          }
        ],
        specialAbilities: [
          'Immune to effects targeting organic legs',
          'Noisy servos: -10 to Stealth while moving'
        ]
      },
      Common: {
        talentGrants: [{ talentId: 'sprint', talentName: 'Sprint' }],
        specialAbilities: ['Immune to effects targeting organic legs']
      },
      Good: {
        modifiers: [
          {
            targetId: 'agility',
            value: 10,
            condition: 'Bionic Locomotion (Good) — movement-related tests'
          }
        ],
        talentGrants: [{ talentId: 'sprint', talentName: 'Sprint' }],
        specialAbilities: ['Immune to effects targeting organic legs']
      },
      Best: {
        modifiers: [
          {
            targetId: 'agility',
            value: 10,
            condition: 'Bionic Locomotion (Best) — movement-related tests'
          }
        ],
        talentGrants: [{ talentId: 'sprint', talentName: 'Sprint' }],
        specialAbilities: ['Immune to effects targeting organic legs', '+1 to base movement rate']
      }
    }
  },

  // -----------------------------------------------------------------
  // Bionic Heart
  // -----------------------------------------------------------------
  'bionic-heart': {
    id: 'bionic-heart',
    name: 'Bionic Heart',
    defaultLocation: 'Body',
    craftsmanshipEffects: {
      Poor: {
        specialAbilities: [
          'Unreliable: occasional stalling under stress',
          'Immune to effects targeting organic heart'
        ]
      },
      Common: {
        talentGrants: [{ talentId: 'sprint', talentName: 'Sprint' }],
        armourBonuses: [{ locations: [BodyLocation.Body], armourPoints: 1 }],
        specialAbilities: ['Immune to effects targeting organic heart', '+1 AP to Body']
      },
      Good: {
        talentGrants: [{ talentId: 'sprint', talentName: 'Sprint' }],
        armourBonuses: [{ locations: [BodyLocation.Body], armourPoints: 2 }],
        specialAbilities: ['Immune to effects targeting organic heart', '+2 AP to Body']
      },
      Best: {
        modifiers: [
          {
            targetId: 'toughness',
            value: 10,
            condition: 'Bionic Heart (Best) — resist fatigue and blood loss'
          }
        ],
        talentGrants: [{ talentId: 'sprint', talentName: 'Sprint' }],
        armourBonuses: [{ locations: [BodyLocation.Body], armourPoints: 2 }],
        specialAbilities: [
          'Immune to effects targeting organic heart',
          '+2 AP to Body',
          '+10 Toughness to resist fatigue and blood loss'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Bionic Respiratory System
  // -----------------------------------------------------------------
  'bionic-respiratory-system': {
    id: 'bionic-respiratory-system',
    name: 'Bionic Respiratory System',
    defaultLocation: 'Body',
    craftsmanshipEffects: {
      Poor: {
        specialAbilities: [
          'Immune to effects targeting organic lungs',
          'Partial toxin filtration: +10 to resist inhaled toxins'
        ]
      },
      Common: {
        specialAbilities: [
          'Immune to effects targeting organic lungs',
          'Toxin filtration: +20 to resist inhaled toxins and gases',
          'Can survive in low-oxygen environments for extended periods'
        ]
      },
      Good: {
        modifiers: [
          {
            targetId: 'toughness',
            value: 10,
            condition: 'Bionic Respiratory System (Good) — resist toxins and suffocation'
          }
        ],
        specialAbilities: [
          'Immune to effects targeting organic lungs',
          'Advanced toxin filtration: immune to most inhaled toxins and gases',
          'Can survive in vacuum for up to Toughness Bonus rounds'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'toughness',
            value: 10,
            condition: 'Bionic Respiratory System (Best) — resist toxins and suffocation'
          }
        ],
        specialAbilities: [
          'Immune to effects targeting organic lungs',
          'Superior toxin filtration: immune to all inhaled toxins and gases',
          'Can survive in vacuum for extended periods',
          'Internal oxygen reserve: no breathing required for Toughness Bonus minutes'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Cybernetic Senses
  // -----------------------------------------------------------------
  'cybernetic-senses': {
    id: 'cybernetic-senses',
    name: 'Cybernetic Senses',
    defaultLocation: 'Head',
    craftsmanshipEffects: {
      Poor: {
        modifiers: [
          {
            targetId: 'perception',
            value: -10,
            condition: 'Cybernetic Senses (Poor) — visual and auditory perception'
          }
        ],
        specialAbilities: [
          'Replacement senses: immune to effects targeting organic eyes/ears',
          'Occasional static or distortion'
        ]
      },
      Common: {
        talentGrants: [
          {
            talentId: 'heightened-senses',
            talentName: 'Heightened Senses',
            specialization: 'Sight'
          }
        ],
        specialAbilities: ['Replacement senses: immune to effects targeting organic eyes/ears']
      },
      Good: {
        modifiers: [
          {
            targetId: 'perception',
            value: 10,
            condition: 'Cybernetic Senses (Good) — visual and auditory perception'
          }
        ],
        talentGrants: [
          {
            talentId: 'heightened-senses',
            talentName: 'Heightened Senses',
            specialization: 'Sight'
          },
          {
            talentId: 'heightened-senses',
            talentName: 'Heightened Senses',
            specialization: 'Sound'
          }
        ],
        specialAbilities: [
          'Replacement senses: immune to effects targeting organic eyes/ears',
          'Enhanced visual and auditory acuity'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'perception',
            value: 20,
            condition: 'Cybernetic Senses (Best) — visual and auditory perception'
          }
        ],
        talentGrants: [
          {
            talentId: 'heightened-senses',
            talentName: 'Heightened Senses',
            specialization: 'Sight'
          },
          {
            talentId: 'heightened-senses',
            talentName: 'Heightened Senses',
            specialization: 'Sound'
          },
          { talentId: 'dark-sight', talentName: 'Dark Sight' }
        ],
        specialAbilities: [
          'Replacement senses: immune to effects targeting organic eyes/ears',
          'Superior visual and auditory acuity',
          'Dark Sight: can see in darkness without penalty',
          'Telescopic and microscopic zoom capability'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Augmented Senses
  // -----------------------------------------------------------------
  'augmented-senses': {
    id: 'augmented-senses',
    name: 'Augmented Senses',
    defaultLocation: 'Head',
    craftsmanshipEffects: {
      Poor: {
        specialAbilities: ['Basic sensory augmentation with occasional interference']
      },
      Common: {
        talentGrants: [
          {
            talentId: 'heightened-senses',
            talentName: 'Heightened Senses',
            specialization: 'Sight'
          }
        ],
        specialAbilities: ['Sensory augmentation overlay enhancing visual input']
      },
      Good: {
        modifiers: [
          {
            targetId: 'perception',
            value: 10,
            condition: 'Augmented Senses (Good) — enhanced sensory input'
          }
        ],
        talentGrants: [
          {
            talentId: 'heightened-senses',
            talentName: 'Heightened Senses',
            specialization: 'Sight'
          },
          { talentId: 'dark-sight', talentName: 'Dark Sight' }
        ],
        specialAbilities: [
          'Sensory augmentation overlay with enhanced spectrum analysis',
          'Dark Sight: can see in darkness without penalty'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'perception',
            value: 20,
            condition: 'Augmented Senses (Best) — superior sensory input'
          }
        ],
        talentGrants: [
          {
            talentId: 'heightened-senses',
            talentName: 'Heightened Senses',
            specialization: 'Sight'
          },
          {
            talentId: 'heightened-senses',
            talentName: 'Heightened Senses',
            specialization: 'Sound'
          },
          { talentId: 'dark-sight', talentName: 'Dark Sight' }
        ],
        specialAbilities: [
          'Superior sensory augmentation with full spectrum analysis',
          'Dark Sight: can see in darkness without penalty',
          'Preysense: detect heat signatures'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Cranial Armour
  // -----------------------------------------------------------------
  'cranial-armour': {
    id: 'cranial-armour',
    name: 'Cranial Armour',
    defaultLocation: 'Head',
    craftsmanshipEffects: {
      Poor: {
        armourBonuses: [{ locations: [BodyLocation.Head], armourPoints: 1 }],
        specialAbilities: [
          'Plasteel and gel padding covering the skull',
          'Prevents concussion and brain injuries',
          'Noticeable scarring and visible implant seams'
        ]
      },
      Common: {
        armourBonuses: [{ locations: [BodyLocation.Head], armourPoints: 1 }],
        specialAbilities: [
          'Plasteel and gel padding covering the skull',
          'Prevents concussion and brain injuries'
        ]
      },
      Good: {
        armourBonuses: [{ locations: [BodyLocation.Head], armourPoints: 2 }],
        specialAbilities: [
          'Reinforced plasteel and gel padding covering the skull',
          'Prevents concussion and brain injuries'
        ]
      },
      Best: {
        armourBonuses: [{ locations: [BodyLocation.Head], armourPoints: 3 }],
        specialAbilities: [
          'Superior plasteel and gel padding covering the skull',
          'Prevents concussion and brain injuries',
          'Nearly undetectable beneath the skin'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Subskin Armour
  // -----------------------------------------------------------------
  'subskin-armour': {
    id: 'subskin-armour',
    name: 'Subskin Armour',
    defaultLocation: 'Body',
    craftsmanshipEffects: {
      Poor: {
        armourBonuses: [
          {
            locations: [
              BodyLocation.Body,
              BodyLocation.LeftArm,
              BodyLocation.RightArm,
              BodyLocation.LeftLeg,
              BodyLocation.RightLeg
            ],
            armourPoints: 1
          }
        ],
        specialAbilities: [
          'Thin carapace plating inserted under the skin',
          'Stacks with any other armour worn',
          'Visible lumps and discolouration'
        ]
      },
      Common: {
        armourBonuses: [
          {
            locations: [
              BodyLocation.Body,
              BodyLocation.LeftArm,
              BodyLocation.RightArm,
              BodyLocation.LeftLeg,
              BodyLocation.RightLeg
            ],
            armourPoints: 2
          }
        ],
        specialAbilities: [
          'Thin carapace plating inserted under the skin',
          'Stacks with any other armour worn'
        ]
      },
      Good: {
        armourBonuses: [
          {
            locations: [
              BodyLocation.Body,
              BodyLocation.LeftArm,
              BodyLocation.RightArm,
              BodyLocation.LeftLeg,
              BodyLocation.RightLeg,
              BodyLocation.Head
            ],
            armourPoints: 2
          }
        ],
        specialAbilities: [
          'Advanced carapace plating inserted under the skin',
          'Stacks with any other armour worn',
          'Covers all body locations including head'
        ]
      },
      Best: {
        armourBonuses: [
          {
            locations: [
              BodyLocation.Body,
              BodyLocation.LeftArm,
              BodyLocation.RightArm,
              BodyLocation.LeftLeg,
              BodyLocation.RightLeg,
              BodyLocation.Head
            ],
            armourPoints: 3
          }
        ],
        specialAbilities: [
          'Superior carapace plating inserted under the skin',
          'Stacks with any other armour worn',
          'Covers all body locations including head',
          'Nearly undetectable beneath the skin'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Synthetic Muscle Grafts (Req 8.3)
  // -----------------------------------------------------------------
  'synthetic-muscle-grafts': {
    id: 'synthetic-muscle-grafts',
    name: 'Synthetic Muscle Grafts',
    defaultLocation: 'Body',
    craftsmanshipEffects: {
      Poor: {
        modifiers: [
          {
            targetId: 'strength',
            value: 5,
            condition: 'Synthetic Muscle Grafts (Poor) — Strength-based tests'
          },
          {
            targetId: 'agility',
            value: -10,
            condition: 'Synthetic Muscle Grafts (Poor) — Agility penalty from bulk'
          }
        ],
        specialAbilities: [
          'Vat-grown hyperdense muscle tissue',
          '+5 Strength',
          '-10 Agility penalty from muscle bulk'
        ]
      },
      Common: {
        modifiers: [
          {
            targetId: 'strength',
            value: 10,
            condition: 'Synthetic Muscle Grafts (Common) — Strength-based tests'
          }
        ],
        specialAbilities: ['Vat-grown hyperdense muscle tissue', 'Adds +10 to Strength-based tests']
      },
      Good: {
        modifiers: [
          {
            targetId: 'strength',
            value: 10,
            condition: 'Synthetic Muscle Grafts (Good) — Strength-based tests'
          },
          {
            targetId: 'toughness',
            value: 5,
            condition: 'Synthetic Muscle Grafts (Good) — enhanced durability'
          }
        ],
        specialAbilities: [
          'Enhanced vat-grown hyperdense muscle tissue',
          '+10 Strength, +5 Toughness'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'strength',
            value: 20,
            condition: 'Synthetic Muscle Grafts (Best) — Strength-based tests'
          },
          {
            targetId: 'toughness',
            value: 10,
            condition: 'Synthetic Muscle Grafts (Best) — enhanced durability'
          }
        ],
        specialAbilities: [
          'Superior vat-grown hyperdense muscle tissue',
          '+20 Strength, +10 Toughness',
          'Grants Unnatural Strength (x2)'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Baleful Eye
  // -----------------------------------------------------------------
  'baleful-eye': {
    id: 'baleful-eye',
    name: 'Baleful Eye',
    defaultLocation: 'Head',
    craftsmanshipEffects: {
      Common: {
        specialAbilities: [
          'Archeotech bionic eye with built-in las weapon (hellpistol equivalent)',
          'Range: 10m, Damage: 1d10+4 E, Pen 7',
          'Can fire even with hands full',
          'May be used as a pistol in melee'
        ]
      },
      Good: {
        modifiers: [
          { targetId: 'perception', value: 10, condition: 'Baleful Eye (Good) — visual perception' }
        ],
        specialAbilities: [
          'Archeotech bionic eye with built-in las weapon (hellpistol equivalent)',
          'Range: 10m, Damage: 1d10+4 E, Pen 7',
          'Can fire even with hands full',
          'May be used as a pistol in melee',
          'Enhanced visual acuity'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'perception',
            value: 10,
            condition: 'Baleful Eye (Best) — visual perception'
          },
          {
            targetId: 'ballisticSkill',
            value: 10,
            condition: 'Baleful Eye (Best) — targeting with eye weapon'
          }
        ],
        specialAbilities: [
          'Archeotech bionic eye with built-in las weapon (hellpistol equivalent)',
          'Range: 10m, Damage: 1d10+4 E, Pen 7',
          'Can fire even with hands full',
          'May be used as a pistol in melee',
          'Superior targeting systems'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Respiratory Filter Implant
  // -----------------------------------------------------------------
  'respiratory-filter-implant': {
    id: 'respiratory-filter-implant',
    name: 'Respiratory Filter Implant',
    defaultLocation: 'Internal',
    craftsmanshipEffects: {
      Poor: {
        specialAbilities: ['Implanted lung filters', '+20 to resist inhaled toxins and gases']
      },
      Common: {
        specialAbilities: [
          'Implanted lung filters',
          'Immune to inhaled toxic gases and atmospheric contaminants'
        ]
      },
      Good: {
        modifiers: [
          {
            targetId: 'toughness',
            value: 10,
            condition: 'Respiratory Filter Implant (Good) — resist airborne hazards'
          }
        ],
        specialAbilities: [
          'Advanced implanted lung filters',
          'Immune to inhaled toxic gases and atmospheric contaminants',
          'Can function in vacuum for short periods'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'toughness',
            value: 10,
            condition: 'Respiratory Filter Implant (Best) — resist airborne hazards'
          }
        ],
        specialAbilities: [
          'Superior implanted lung filters',
          'Immune to all inhaled toxins, gases, and atmospheric contaminants',
          'Can function in vacuum for Toughness Bonus rounds',
          'Internal oxygen reserve'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Voidskin
  // -----------------------------------------------------------------
  voidskin: {
    id: 'voidskin',
    name: 'Voidskin',
    defaultLocation: 'Body',
    craftsmanshipEffects: {
      Poor: {
        specialAbilities: [
          'Subdermal skin treated with flak weave and chemical toughening',
          'Resist void exposure for additional 1d5 rounds'
        ]
      },
      Common: {
        specialAbilities: [
          'Subdermal skin treated with flak weave and chemical toughening',
          'Resist void exposure for additional 1d10+3 rounds'
        ]
      },
      Good: {
        modifiers: [
          {
            targetId: 'toughness',
            value: 5,
            condition: 'Voidskin (Good) — resist environmental hazards'
          }
        ],
        specialAbilities: [
          'Advanced subdermal skin treatment',
          'Resist void exposure for additional 1d10+5 rounds',
          '+5 Toughness against environmental hazards'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'toughness',
            value: 10,
            condition: 'Voidskin (Best) — resist environmental hazards'
          }
        ],
        specialAbilities: [
          'Superior subdermal skin treatment',
          'Resist void exposure for additional 2d10+5 rounds',
          '+10 Toughness against environmental hazards'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Volitor Implant
  // -----------------------------------------------------------------
  'volitor-implant': {
    id: 'volitor-implant',
    name: 'Volitor Implant',
    defaultLocation: 'Internal',
    craftsmanshipEffects: {
      Common: {
        specialAbilities: [
          'Neural compulsion implant',
          'Compels subject not to reveal certain information, leave a set area, or perform a specific task',
          'Attempting to counter the compulsion causes unconsciousness or death'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Vox Implant
  // -----------------------------------------------------------------
  'vox-implant': {
    id: 'vox-implant',
    name: 'Vox Implant',
    defaultLocation: 'Internal',
    craftsmanshipEffects: {
      Common: {
        specialAbilities: [
          'Built-in micro-bead vox communicator',
          'Hands-free communication',
          'Often hardwired to ship vox frequencies'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Locator Matrix
  // -----------------------------------------------------------------
  'locator-matrix': {
    id: 'locator-matrix',
    name: 'Locator Matrix',
    defaultLocation: 'Internal',
    craftsmanshipEffects: {
      Common: {
        specialAbilities: [
          'Micro-cogitators providing awareness of magnetic north, location, velocity, altitude, and time',
          'Requires maps to benefit from location data'
        ]
      },
      Good: {
        modifiers: [
          {
            targetId: 'navigate-surface',
            value: 10,
            condition: 'Locator Matrix (Good) — navigation with location data'
          }
        ],
        specialAbilities: [
          'Enhanced micro-cogitators providing precise location awareness',
          '+10 to Navigate (Surface) tests'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'navigate-surface',
            value: 20,
            condition: 'Locator Matrix (Best) — navigation with location data'
          }
        ],
        specialAbilities: [
          'Superior micro-cogitators providing precise location awareness',
          '+20 to Navigate (Surface) tests',
          'Integrated auspex overlay'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Scribe-tines
  // -----------------------------------------------------------------
  'scribe-tines': {
    id: 'scribe-tines',
    name: 'Scribe-tines',
    defaultLocation: 'Right Arm',
    craftsmanshipEffects: {
      Poor: {
        modifiers: [
          {
            targetId: 'investigation',
            value: 5,
            condition: 'Scribe-tines (Poor) — data-slate and document handling'
          }
        ],
        specialAbilities: [
          'Hand and forearm replaced with specialised tools',
          'Page turning, autoscribing, and data-slate manipulation'
        ]
      },
      Common: {
        modifiers: [
          {
            targetId: 'investigation',
            value: 10,
            condition: 'Scribe-tines (Common) — data-slate and document handling'
          }
        ],
        specialAbilities: [
          'Hand and forearm replaced with specialised tools',
          '+10 to all Investigation Skill Tests'
        ]
      },
      Good: {
        modifiers: [
          {
            targetId: 'investigation',
            value: 10,
            condition: 'Scribe-tines (Good) — data-slate and document handling'
          },
          {
            targetId: 'literacy',
            value: 10,
            condition: 'Scribe-tines (Good) — reading and writing'
          }
        ],
        specialAbilities: [
          'Enhanced hand and forearm tools',
          '+10 to Investigation and Literacy tests'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'investigation',
            value: 20,
            condition: 'Scribe-tines (Best) — data-slate and document handling'
          },
          {
            targetId: 'literacy',
            value: 10,
            condition: 'Scribe-tines (Best) — reading and writing'
          }
        ],
        specialAbilities: [
          'Superior hand and forearm tools',
          '+20 to Investigation tests, +10 to Literacy tests',
          'Integrated data-slate with wireless connectivity'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Cortex Implants
  // -----------------------------------------------------------------
  'cortex-implants': {
    id: 'cortex-implants',
    name: 'Cortex Implants',
    defaultLocation: 'Head',
    craftsmanshipEffects: {
      Poor: {
        modifiers: [
          {
            targetId: 'weaponSkill',
            value: -10,
            condition: 'Cortex Implants (Poor) — brain damage side effects'
          },
          {
            targetId: 'ballisticSkill',
            value: -10,
            condition: 'Cortex Implants (Poor) — brain damage side effects'
          },
          {
            targetId: 'agility',
            value: -10,
            condition: 'Cortex Implants (Poor) — brain damage side effects'
          },
          {
            targetId: 'intelligence',
            value: -10,
            condition: 'Cortex Implants (Poor) — brain damage side effects'
          },
          {
            targetId: 'fellowship',
            value: -10,
            condition: 'Cortex Implants (Poor) — brain damage side effects'
          }
        ],
        specialAbilities: [
          'Brain repair implant — destroys personality (servitor-level)',
          'Permanent characteristic penalties',
          'Gains 1d10 Insanity Points'
        ]
      },
      Common: {
        specialAbilities: [
          'Brain repair/augmentation implant',
          'Restores brain-damaged individuals to basic function',
          'Permanent -1d10 to WS, BS, Ag, Int, Fel (applied at installation)',
          'Gains 1d10 Insanity Points'
        ]
      },
      Good: {
        modifiers: [
          {
            targetId: 'intelligence',
            value: 10,
            condition: 'Cortex Implants (Good) — cogitator-enhanced cognition'
          }
        ],
        specialAbilities: [
          'Advanced brain augmentation with cogitator functions',
          'Grants Unnatural Intelligence (x2)',
          '+10 Intelligence',
          'Gains 1d10 Insanity Points'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'intelligence',
            value: 20,
            condition: 'Cortex Implants (Best) — superior cogitator-enhanced cognition'
          }
        ],
        specialAbilities: [
          'Superior brain augmentation with advanced cogitator functions',
          'Grants Unnatural Intelligence (x2)',
          '+20 Intelligence',
          'Minimal personality disruption'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Mind Impulse Unit (Req 8.5)
  // -----------------------------------------------------------------
  'mind-impulse-unit': {
    id: 'mind-impulse-unit',
    name: 'Mind Impulse Unit',
    defaultLocation: 'Head',
    craftsmanshipEffects: {
      Poor: {
        specialAbilities: [
          'Direct machine interface via spinal/cortex connector',
          'Requires Willpower Test to use',
          '-10 to interact with machine spirits'
        ]
      },
      Common: {
        modifiers: [
          {
            targetId: 'tech-use',
            value: 10,
            condition: 'Mind Impulse Unit (Common) — using MIU-compatible devices'
          },
          {
            targetId: 'pilot-spacecraft',
            value: 10,
            condition: 'Mind Impulse Unit (Common) — using MIU-linked controls'
          },
          {
            targetId: 'pilot-flyers',
            value: 10,
            condition: 'Mind Impulse Unit (Common) — using MIU-linked controls'
          },
          {
            targetId: 'drive-ground-vehicle',
            value: 10,
            condition: 'Mind Impulse Unit (Common) — using MIU-linked controls'
          },
          {
            targetId: 'drive-skimmer-hovercraft',
            value: 10,
            condition: 'Mind Impulse Unit (Common) — using MIU-linked controls'
          },
          {
            targetId: 'drive-walker',
            value: 10,
            condition: 'Mind Impulse Unit (Common) — using MIU-linked controls'
          }
        ],
        specialAbilities: [
          'Direct machine interface via spinal/cortex connector',
          '+10 to Tech-Use, Pilot, and Drive Tests when using MIU-compatible devices'
        ]
      },
      Good: {
        modifiers: [
          {
            targetId: 'tech-use',
            value: 10,
            condition: 'Mind Impulse Unit (Good) — using MIU-compatible devices'
          },
          {
            targetId: 'pilot-spacecraft',
            value: 10,
            condition: 'Mind Impulse Unit (Good) — using MIU-linked controls'
          },
          {
            targetId: 'pilot-flyers',
            value: 10,
            condition: 'Mind Impulse Unit (Good) — using MIU-linked controls'
          },
          {
            targetId: 'drive-ground-vehicle',
            value: 10,
            condition: 'Mind Impulse Unit (Good) — using MIU-linked controls'
          },
          {
            targetId: 'drive-skimmer-hovercraft',
            value: 10,
            condition: 'Mind Impulse Unit (Good) — using MIU-linked controls'
          },
          {
            targetId: 'drive-walker',
            value: 10,
            condition: 'Mind Impulse Unit (Good) — using MIU-linked controls'
          },
          {
            targetId: 'ballisticSkill',
            value: 10,
            condition: 'Mind Impulse Unit (Good) — firing MIU-linked weapons'
          }
        ],
        specialAbilities: [
          'Direct machine interface via spinal/cortex connector',
          '+10 to Tech-Use, Pilot, Drive, and BS when interfaced with MIU-linked devices',
          '+10 to communicate with machine spirits',
          'Can experience familiar senses through MIU link'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'tech-use',
            value: 20,
            condition: 'Mind Impulse Unit (Best) — using MIU-compatible devices'
          },
          {
            targetId: 'pilot-spacecraft',
            value: 10,
            condition: 'Mind Impulse Unit (Best) — using MIU-linked controls'
          },
          {
            targetId: 'pilot-flyers',
            value: 10,
            condition: 'Mind Impulse Unit (Best) — using MIU-linked controls'
          },
          {
            targetId: 'drive-ground-vehicle',
            value: 10,
            condition: 'Mind Impulse Unit (Best) — using MIU-linked controls'
          },
          {
            targetId: 'drive-skimmer-hovercraft',
            value: 10,
            condition: 'Mind Impulse Unit (Best) — using MIU-linked controls'
          },
          {
            targetId: 'drive-walker',
            value: 10,
            condition: 'Mind Impulse Unit (Best) — using MIU-linked controls'
          },
          {
            targetId: 'ballisticSkill',
            value: 10,
            condition: 'Mind Impulse Unit (Best) — firing MIU-linked weapons'
          }
        ],
        specialAbilities: [
          'Direct machine interface via spinal/cortex connector',
          '+20 to Tech-Use when using MIU-compatible devices',
          '+10 to Pilot, Drive, and BS when interfaced with MIU-linked devices',
          '+10 to communicate with machine spirits',
          'Can experience familiar senses through MIU link',
          'Seamless neural integration with minimal lag'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Augur Array
  // -----------------------------------------------------------------
  'augur-array': {
    id: 'augur-array',
    name: 'Augur Array',
    defaultLocation: 'Internal',
    craftsmanshipEffects: {
      Poor: {
        specialAbilities: [
          'Implanted sensor system with limited function',
          'Single detection ability (heat, radiation, or electromagnetics)',
          '20m detection range',
          'Requires Half Action to use'
        ]
      },
      Common: {
        specialAbilities: [
          'Implanted sensor system duplicating auspex functions',
          'Functions as a handheld auspex',
          'Requires Half Action to use'
        ]
      },
      Good: {
        modifiers: [
          {
            targetId: 'awareness',
            value: 10,
            condition: 'Augur Array (Good) — using implanted sensors'
          }
        ],
        specialAbilities: [
          'Advanced implanted sensor system',
          'Full auspex functions with enhanced range',
          'May re-roll failed Perception Tests when using sensor functions',
          'Requires Half Action to use'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'awareness',
            value: 20,
            condition: 'Augur Array (Best) — using implanted sensors'
          }
        ],
        specialAbilities: [
          'Superior implanted sensor system',
          'Full auspex functions with superior range and resolution',
          'May re-roll failed Perception Tests when using sensor functions',
          'Passive scanning mode: no action required for basic detection'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Calculus Logi Upgrade
  // -----------------------------------------------------------------
  'calculus-logi-upgrade': {
    id: 'calculus-logi-upgrade',
    name: 'Calculus Logi Upgrade',
    defaultLocation: 'Head',
    craftsmanshipEffects: {
      Poor: {
        modifiers: [
          {
            targetId: 'logic',
            value: 5,
            condition: 'Calculus Logi Upgrade (Poor) — logic and calculation tasks'
          }
        ],
        specialAbilities: [
          'Internal cogitator implants for data retention and processing',
          'Intermittent processing delays'
        ]
      },
      Common: {
        modifiers: [
          {
            targetId: 'literacy',
            value: 10,
            condition: 'Calculus Logi Upgrade (Common) — reading and data processing'
          },
          {
            targetId: 'logic',
            value: 10,
            condition: 'Calculus Logi Upgrade (Common) — logic and calculation tasks'
          }
        ],
        specialAbilities: [
          'Internal cogitator implants for data retention and processing',
          '+10 to Literacy, Logic, and Scholastic Lore Tests'
        ]
      },
      Good: {
        modifiers: [
          {
            targetId: 'literacy',
            value: 10,
            condition: 'Calculus Logi Upgrade (Good) — reading and data processing'
          },
          {
            targetId: 'logic',
            value: 20,
            condition: 'Calculus Logi Upgrade (Good) — logic and calculation tasks'
          }
        ],
        specialAbilities: [
          'Enhanced internal cogitator implants',
          '+10 to Literacy Tests, +20 to Logic and Scholastic Lore Tests',
          'Rapid data cross-referencing capability'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'literacy',
            value: 20,
            condition: 'Calculus Logi Upgrade (Best) — reading and data processing'
          },
          {
            targetId: 'logic',
            value: 20,
            condition: 'Calculus Logi Upgrade (Best) — logic and calculation tasks'
          },
          {
            targetId: 'intelligence',
            value: 10,
            condition: 'Calculus Logi Upgrade (Best) — cogitator-enhanced cognition'
          }
        ],
        specialAbilities: [
          'Superior internal cogitator implants',
          '+20 to Literacy, Logic, and Scholastic Lore Tests',
          '+10 Intelligence from cogitator-enhanced cognition',
          'Near-instantaneous data processing and recall'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Memorance Implant
  // -----------------------------------------------------------------
  'memorance-implant': {
    id: 'memorance-implant',
    name: 'Memorance Implant',
    defaultLocation: 'Head',
    craftsmanshipEffects: {
      Poor: {
        specialAbilities: [
          'Neurally linked datavault and pict-capture array',
          'Records information but playback is unreliable',
          'Occasional data corruption'
        ]
      },
      Common: {
        talentGrants: [{ talentId: 'total-recall', talentName: 'Total Recall' }],
        specialAbilities: [
          'Neurally linked datavault and pict-capture array',
          'Records information of people and scenes viewed',
          'Can replay or overlay recorded data',
          '+10 to social Tests where recorded info provides leverage',
          'Grants Total Recall Talent'
        ]
      },
      Good: {
        modifiers: [
          {
            targetId: 'intelligence',
            value: 10,
            condition: 'Memorance Implant (Good) — data recall and analysis'
          }
        ],
        talentGrants: [{ talentId: 'total-recall', talentName: 'Total Recall' }],
        specialAbilities: [
          'Enhanced neurally linked datavault and pict-capture array',
          'High-fidelity recording with enhanced playback',
          'Can replay or overlay recorded data with zoom and enhancement',
          '+10 to social Tests where recorded info provides leverage',
          'Grants Total Recall Talent'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'intelligence',
            value: 10,
            condition: 'Memorance Implant (Best) — data recall and analysis'
          }
        ],
        talentGrants: [{ talentId: 'total-recall', talentName: 'Total Recall' }],
        specialAbilities: [
          'Superior neurally linked datavault and pict-capture array',
          'Perfect fidelity recording with instant recall',
          'Can replay or overlay recorded data with full analysis suite',
          '+10 to social Tests where recorded info provides leverage',
          'Grants Total Recall Talent',
          'Wireless data transfer to compatible cogitators'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // MIU Weapon Interface
  // -----------------------------------------------------------------
  'miu-weapon-interface': {
    id: 'miu-weapon-interface',
    name: 'MIU Weapon Interface',
    defaultLocation: 'Shoulder',
    craftsmanshipEffects: {
      Poor: {
        modifiers: [
          {
            targetId: 'ballisticSkill',
            value: -10,
            condition: 'MIU Weapon Interface (Poor) — firing shoulder-mounted weapon'
          }
        ],
        specialAbilities: [
          'Simplified MIU for remote operation of a shoulder-mounted ranged weapon',
          'Fire as a Free Action but with -10 BS penalty'
        ]
      },
      Common: {
        specialAbilities: [
          'Simplified MIU for remote operation of a shoulder-mounted ranged weapon',
          'Fire as a Free Action using full BS, regardless of other actions taken that turn'
        ]
      },
      Good: {
        modifiers: [
          {
            targetId: 'ballisticSkill',
            value: 5,
            condition: 'MIU Weapon Interface (Good) — firing shoulder-mounted weapon'
          }
        ],
        specialAbilities: [
          'Enhanced MIU for remote operation of a shoulder-mounted ranged weapon',
          'Fire as a Free Action using full BS +5, regardless of other actions taken that turn'
        ]
      },
      Best: {
        modifiers: [
          {
            targetId: 'ballisticSkill',
            value: 10,
            condition: 'MIU Weapon Interface (Best) — firing shoulder-mounted weapon'
          }
        ],
        specialAbilities: [
          'Superior MIU for remote operation of a shoulder-mounted ranged weapon',
          'Fire as a Free Action using full BS +10, regardless of other actions taken that turn',
          'Enhanced targeting link with predictive tracking'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Ballistic Mechadendrite
  // -----------------------------------------------------------------
  'ballistic-mechadendrite': {
    id: 'ballistic-mechadendrite',
    name: 'Ballistic Mechadendrite',
    defaultLocation: 'Shoulder',
    proficiencyCategory: 'Weapon',
    weaponEntry: {
      class: WeaponClass.Pistol,
      range: 15,
      rateOfFire: { single: 1, semiAuto: 0, fullAuto: 0 },
      damage: '1d10+2',
      damageType: 'E',
      penetration: 0,
      clip: 30,
      reload: 'Half',
      special: ['Compact', 'Reliable'],
      fireRestriction: 'Once per round, as Reaction or Half Action'
    },
    craftsmanshipEffects: {
      Common: {
        specialAbilities: [
          'Fire as Reaction or Half Action, once per round',
          'Uses full Ballistic Skill'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Manipulator Mechadendrite
  // -----------------------------------------------------------------
  'manipulator-mechadendrite': {
    id: 'manipulator-mechadendrite',
    name: 'Manipulator Mechadendrite',
    defaultLocation: 'Shoulder',
    proficiencyCategory: 'Utility',
    weaponEntry: {
      class: WeaponClass.Melee,
      range: 0,
      rateOfFire: { single: 0, semiAuto: 0, fullAuto: 0 },
      damage: '1d5+2',
      damageType: 'I',
      penetration: 0,
      clip: 0,
      reload: '-',
      special: ['Primitive']
    },
    craftsmanshipEffects: {
      Common: {
        modifiers: [
          {
            targetId: 'strength',
            value: 20,
            condition: 'Strength-based tests (Manipulator mechadendrite)'
          }
        ],
        specialAbilities: [
          '+20 to Strength Tests',
          'Can tether to anchor point as Free Action',
          'Cannot perform fine manipulation'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Medicae Mechadendrite
  // -----------------------------------------------------------------
  'medicae-mechadendrite': {
    id: 'medicae-mechadendrite',
    name: 'Medicae Mechadendrite',
    defaultLocation: 'Shoulder',
    proficiencyCategory: 'Utility',
    weaponEntry: {
      class: WeaponClass.Melee,
      range: 0,
      rateOfFire: { single: 0, semiAuto: 0, fullAuto: 0 },
      damage: '1d5',
      damageType: 'R',
      penetration: 0,
      clip: 0,
      reload: '-',
      special: []
    },
    craftsmanshipEffects: {
      Common: {
        modifiers: [
          { targetId: 'medicae', value: 10, condition: 'Medicae mechadendrite equipped' },
          { targetId: 'interrogation', value: 10, condition: 'Medicae mechadendrite equipped' }
        ],
        specialAbilities: [
          '+10 to Medicae Tests',
          '+10 to Interrogation Tests',
          '6 injector pistons for drugs',
          'Staunch Blood Loss as Half Action',
          'Chainscalpel: limb amputation at Challenging (+0)'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Optical Mechadendrite
  // -----------------------------------------------------------------
  'optical-mechadendrite': {
    id: 'optical-mechadendrite',
    name: 'Optical Mechadendrite',
    defaultLocation: 'Shoulder',
    proficiencyCategory: 'Utility',
    craftsmanshipEffects: {
      Common: {
        modifiers: [
          { targetId: 'perception', value: 10, condition: 'Optical mechadendrite equipped' },
          {
            targetId: 'perception',
            value: 20,
            condition: 'Vision-based Perception at night (Optical mechadendrite)'
          }
        ],
        specialAbilities: [
          '+10 to all Perception-based Tests',
          '+20 to vision-based Perception Tests at night',
          'No penalties due to darkness',
          'Microscopic and telescopic examination',
          'Infrared torch and sensors',
          'Adjustable light'
        ]
      }
    }
  },

  // -----------------------------------------------------------------
  // Utility Mechadendrite
  // -----------------------------------------------------------------
  'utility-mechadendrite': {
    id: 'utility-mechadendrite',
    name: 'Utility Mechadendrite',
    defaultLocation: 'Shoulder',
    proficiencyCategory: 'Utility',
    weaponEntry: {
      class: WeaponClass.Melee,
      range: 0,
      rateOfFire: { single: 0, semiAuto: 0, fullAuto: 0 },
      damage: '1d5',
      damageType: 'R',
      penetration: 2,
      clip: 0,
      reload: '-',
      special: ['Defensive', 'Mono']
    },
    craftsmanshipEffects: {
      Common: {
        modifiers: [
          {
            targetId: 'tech-use',
            value: 10,
            condition: 'Counts as combi-tool (Utility mechadendrite)'
          }
        ],
        specialAbilities: [
          'Counts as combi-tool (+10 Tech-Use)',
          '6 injector pistons for sacred unguent',
          'Incense censer: -5 WS to living creatures within 2m for 1 round (Half Action, 1 blast per 15 min)',
          'Cutting blade (knife with Defensive + Mono)'
        ]
      }
    }
  }
};
